import * as XLSX from "xlsx"
import { parseBsZpRow } from "@/lib/bs-zp-parser"
import { appLogger } from "@/lib/appLogger"

function normalizeString(str: any) {
  if (!str) return ""
  return str
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[^a-z0-9]/g, "")
}

function getBaseUrl(): string {
  try {
    const raw = (import.meta as any)?.env?.BASE_URL
    const base = String(raw || "/")
    if (!base || base === "undefined" || base === "null") return "/"
    return base.endsWith("/") ? base : `${base}/`
  } catch {
    return "/"
  }
}

function buildAssetCandidates(relPath: string): string[] {
  const p = String(relPath || "").replace(/^\/+/, "")
  if (!p) return []
  const base = getBaseUrl()
  const viaBase = `${base}${p}`
  const viaRoot = `/${p}`
  if (viaBase === viaRoot) return [viaRoot]
  return [viaBase, viaRoot]
}

function dedupeUrls(urls: string[]): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const u of urls) {
    const key = String(u || "")
    if (!key) continue
    if (seen.has(key)) continue
    seen.add(key)
    out.push(key)
  }
  return out
}

let fetchCacheBuster = Date.now()

function withCacheBuster(url: string): string {
  const u = String(url || "")
  if (!u) return u
  if (u.includes("t=")) return u
  const join = u.includes("?") ? "&" : "?"
  return `${u}${join}t=${fetchCacheBuster}`
}

async function fetchNoStore(url: string): Promise<Response> {
  return fetch(withCacheBuster(url), { cache: "no-store" })
}

class BsZpLoadError extends Error {
  code: "FILE_NOT_FOUND" | "BAD_RESPONSE" | "BAD_FILE" | "TIMEOUT" | "PARSE_FAILED"
  meta: Record<string, unknown>
  constructor(code: BsZpLoadError["code"], message: string, meta?: Record<string, unknown>) {
    super(message)
    this.name = "BsZpLoadError"
    this.code = code
    this.meta = meta ?? {}
  }
}

async function fetchWithTimeout(url: string, timeoutMs: number): Promise<Response> {
  if (typeof AbortController === "undefined") return fetchNoStore(url)
  const ac = new AbortController()
  const t = window.setTimeout(() => ac.abort(), timeoutMs)
  try {
    return await fetch(withCacheBuster(url), { cache: "no-store", signal: ac.signal })
  } catch (e: any) {
    if (e?.name === "AbortError") {
      throw new BsZpLoadError("TIMEOUT", `Request timed out for ${url}`, { url, timeoutMs })
    }
    throw e
  } finally {
    window.clearTimeout(t)
  }
}

function isZipBuffer(buf: ArrayBuffer): boolean {
  try {
    const u8 = new Uint8Array(buf)
    return u8.length >= 2 && u8[0] === 0x50 && u8[1] === 0x4b
  } catch {
    return false
  }
}

async function loadFirstAvailableBinary(
  label: "BS" | "ZP",
  urls: string[],
  timeoutMs: number,
): Promise<{ url: string; buf: ArrayBuffer }> {
  const tried: Array<{ url: string; status?: number; contentType?: string | null; signature?: number[] }> = []
  let sawOkNonZip = false
  for (const url of urls) {
    let res: Response
    try {
      console.log("FETCH URL:", url)
      res = await fetchWithTimeout(url, timeoutMs)
    } catch (e: any) {
      const msg = e instanceof Error ? e.message : String(e)
      tried.push({ url, status: undefined, contentType: null })
      appLogger.warning(`${label} data fetch failed`, { category: "data", meta: { url, error: msg } })
      continue
    }

    const contentType = res.headers?.get?.("content-type") ?? null
    tried.push({ url, status: res.status, contentType })
    if (!res.ok) continue

    let buf: ArrayBuffer
    try {
      buf = await res.arrayBuffer()
    } catch (e: any) {
      const msg = e instanceof Error ? e.message : String(e)
      throw new BsZpLoadError("BAD_RESPONSE", `${label} data response unreadable`, { url, status: res.status, contentType, error: msg })
    }

    if (!isZipBuffer(buf)) {
      sawOkNonZip = true
      const signature = Array.from(new Uint8Array(buf.slice(0, 8)))
      tried[tried.length - 1] = { url, status: res.status, contentType, signature }
      appLogger.warning(`${label} XLSX signature mismatch`, {
        category: "data",
        meta: { url, status: res.status, contentType, signature },
      })
      continue
    }

    return { url, buf }
  }

  if (sawOkNonZip) {
    throw new BsZpLoadError("BAD_FILE", `${label} XLSX file is missing or invalid`, { tried })
  }
  throw new BsZpLoadError("FILE_NOT_FOUND", `${label} data file not found on server`, { tried })
}

// --- Interfaces ---

export type GPTranslation = V2Names;

export interface V2Names {
  en: string
  hi: string
  pa: string
}

export interface WardInfo {
  number: number
  en: string
  hi: string
  pa: string
  [key: string]: any
}

export interface V2Ward {
  number: number
  names: V2Names
}

export interface V2GP {
  id: string
  names: V2Names
  sarpanch: V2Names
  wards: V2Ward[]
}

export interface V2Block {
  id: string
  names: V2Names
  gps: V2GP[]
}

export interface V2GPShard {
  id: string
  names: V2Names
  blocks: V2Block[]
}

export interface V2HierarchyDistrict {
  id: string
  names: V2Names
  blocks: {
    id: string
    names: V2Names
    gps: {
      id: string
      names: V2Names
    }[]
  }[]
}

export interface V2Hierarchy {
  districts: V2HierarchyDistrict[]
}

// --- BS & ZP Interfaces ---

export interface BsZpPerson {
  names: V2Names
}

export interface BsZpWard {
  number: number
  names: V2Names
}

export interface BsData {
  id: string // Block ID
  names: V2Names
  districtId: string
  chairperson: BsZpPerson
  viceChairperson?: BsZpPerson
  wards: BsZpWard[]
}

export interface ZpData {
  id: string // District ID
  names: V2Names
  chairperson: BsZpPerson
  viceChairperson?: BsZpPerson
  wards: BsZpWard[]
}

// --- GIS Data ---
export interface GisData {
  lat: number
  lon: number
}

let gisCache: Record<string, GisData> | null = null

export type GisDistrictCoordsErrorCode =
  | "FILE_NOT_FOUND"
  | "FILE_CORRUPTED"
  | "DISTRICT_NOT_FOUND"
  | "FILTER_COLUMN_NOT_EMPTY"
  | "INVALID_COORDINATES"

export class GisDistrictCoordsError extends Error {
  code: GisDistrictCoordsErrorCode
  constructor(code: GisDistrictCoordsErrorCode, message: string) {
    super(message)
    this.code = code
    this.name = "GisDistrictCoordsError"
  }
}

type GisDistrictRow = {
  districtRaw: string
  filterRaw: string
  latRaw: string
  lonRaw: string
  rowIndex: number
}

type GisDistrictParsed = {
  rows: GisDistrictRow[]
  index: Record<string, GisData>
}

let gisDistrictCache: GisDistrictParsed | null = null

function parseEmptyCell(value: unknown): string {
  return String(value ?? "").trim()
}

function parseNumberCell(value: unknown): number {
  const s = String(value ?? "").trim()
  if (!s) return Number.NaN
  const n = Number(s)
  return Number.isFinite(n) ? n : Number.NaN
}

function validLatLon(lat: number, lon: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180
}

export function extractDistrictCoordinatesFromGisblockRows(inputRows: unknown[][], districtName: string): GisData {
  const targetKey = normalizeString(districtName)
  if (!targetKey) {
    throw new GisDistrictCoordsError("DISTRICT_NOT_FOUND", "District name is empty")
  }

  const rows = (inputRows || []) as unknown[][]
  const candidates: GisDistrictRow[] = []

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i] || []
    const districtRaw = parseEmptyCell((row as any)[0])
    if (!districtRaw) continue
    if (normalizeString(districtRaw) !== targetKey) continue
    candidates.push({
      districtRaw,
      filterRaw: parseEmptyCell((row as any)[1]),
      latRaw: parseEmptyCell((row as any)[2]),
      lonRaw: parseEmptyCell((row as any)[3]),
      rowIndex: i,
    })
  }

  if (candidates.length === 0) {
    throw new GisDistrictCoordsError("DISTRICT_NOT_FOUND", `District not found in GISBLOCK.xlsx: ${districtName}`)
  }

  const filtered = candidates.filter((c) => parseEmptyCell(c.filterRaw) === "")
  if (filtered.length === 0) {
    throw new GisDistrictCoordsError(
      "FILTER_COLUMN_NOT_EMPTY",
      `District found but filter column is not empty for: ${districtName}`
    )
  }

  const valid = filtered
    .map((c) => {
      const lat = parseNumberCell(c.latRaw)
      const lon = parseNumberCell(c.lonRaw)
      return { c, lat, lon }
    })
    .filter((x) => validLatLon(x.lat, x.lon))

  if (valid.length === 0) {
    throw new GisDistrictCoordsError(
      "INVALID_COORDINATES",
      `District rows found but coordinates are invalid for: ${districtName}`
    )
  }

  const chosen = valid[0]
  const unique = new Map<string, number>()
  for (const item of valid) {
    unique.set(`${item.lat}|${item.lon}`, (unique.get(`${item.lat}|${item.lon}`) || 0) + 1)
  }
  if (unique.size > 1) {
    console.warn("[GISBLOCK] Multiple coordinate pairs found for district; using first match", {
      district: districtName,
      matches: Array.from(unique.keys()),
      chosen: `${chosen.lat}|${chosen.lon}`,
    })
  } else if (valid.length > 1) {
    console.debug("[GISBLOCK] Duplicate district coordinate rows found; using first match", {
      district: districtName,
      count: valid.length,
      chosen: `${chosen.lat}|${chosen.lon}`,
    })
  }

  return { lat: chosen.lat, lon: chosen.lon }
}

async function loadGisDistrictData(): Promise<GisDistrictParsed> {
  if (gisDistrictCache) return gisDistrictCache

  const timestamp = Date.now()
  const url = `/data/GISBLOCK.xlsx?t=${timestamp}`

  let res: Response
  try {
    res = await fetch(url)
  } catch (e: any) {
    console.error("[GISBLOCK] Fetch failed", e)
    throw new GisDistrictCoordsError("FILE_NOT_FOUND", "Failed to fetch GISBLOCK.xlsx")
  }

  if (!res.ok) {
    throw new GisDistrictCoordsError("FILE_NOT_FOUND", `GISBLOCK.xlsx not found (HTTP ${res.status})`)
  }

  let buf: ArrayBuffer
  try {
    buf = await res.arrayBuffer()
  } catch (e: any) {
    console.error("[GISBLOCK] Failed to read arrayBuffer", e)
    throw new GisDistrictCoordsError("FILE_CORRUPTED", "GISBLOCK.xlsx is corrupted or unreadable")
  }

  let wb: XLSX.WorkBook
  try {
    wb = XLSX.read(buf, { type: "array", raw: false })
  } catch (e: any) {
    console.error("[GISBLOCK] XLSX.read failed", e)
    throw new GisDistrictCoordsError("FILE_CORRUPTED", "GISBLOCK.xlsx is corrupted or invalid")
  }

  const sheetNames = wb.SheetNames || []
  const sheetName = sheetNames.find((n) => n.toLowerCase().includes("district")) || sheetNames[0]
  if (!sheetName) {
    throw new GisDistrictCoordsError("FILE_CORRUPTED", "GISBLOCK.xlsx has no sheets")
  }

  const ws = wb.Sheets[sheetName]
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" }) as unknown[][]

  const index: Record<string, GisData> = {}
  const districtRows: GisDistrictRow[] = []

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i] || []
    const districtRaw = parseEmptyCell((row as any)[0])
    if (!districtRaw) continue

    const filterRaw = parseEmptyCell((row as any)[1])
    const latRaw = parseEmptyCell((row as any)[2])
    const lonRaw = parseEmptyCell((row as any)[3])

    districtRows.push({ districtRaw, filterRaw, latRaw, lonRaw, rowIndex: i })

    if (filterRaw !== "") continue
    const lat = parseNumberCell(latRaw)
    const lon = parseNumberCell(lonRaw)
    if (!validLatLon(lat, lon)) continue

    const key = normalizeString(districtRaw)
    if (!key) continue
    if (!index[key]) {
      index[key] = { lat, lon }
      continue
    }
    const existing = index[key]
    if (existing.lat !== lat || existing.lon !== lon) {
      console.warn("[GISBLOCK] Conflicting district coordinates; keeping first", {
        district: districtRaw,
        existing,
        incoming: { lat, lon },
      })
    }
  }

  gisDistrictCache = { rows: districtRows, index }
  console.log(`[GISBLOCK] District GIS index loaded. Districts: ${Object.keys(index).length}, Rows: ${districtRows.length}`)
  return gisDistrictCache
}

export async function getDistrictCoordinatesFromGisblock(districtName: string): Promise<GisData> {
  const parsed = await loadGisDistrictData()
  const key = normalizeString(districtName)
  if (key && parsed.index[key]) return parsed.index[key]
  return extractDistrictCoordinatesFromGisblockRows(
    parsed.rows.map((r) => [r.districtRaw, r.filterRaw, r.latRaw, r.lonRaw]),
    districtName
  )
}

export async function loadGisData(): Promise<Record<string, GisData>> {
  if (gisCache) return gisCache
  try {
    const timestamp = Date.now();
    try {
      const jsonRes = await fetch(`/data/gis_locations.min.json?t=${timestamp}`)
      if (jsonRes.ok) {
        const j = await jsonRes.json() as any
        const cache: Record<string, GisData> = {}
        const blocks = j?.blocks && typeof j.blocks === 'object' ? j.blocks : null
        const gps = j?.gps && typeof j.gps === 'object' ? j.gps : null
        if (blocks) {
          for (const [k, v] of Object.entries(blocks)) {
            const lat = Number((v as any)?.lat)
            const lon = Number((v as any)?.lon)
            if (Number.isFinite(lat) && Number.isFinite(lon)) cache[String(k)] = { lat, lon }
          }
        }
        if (gps) {
          for (const [k, v] of Object.entries(gps)) {
            const lat = Number((v as any)?.lat)
            const lon = Number((v as any)?.lon)
            if (Number.isFinite(lat) && Number.isFinite(lon)) cache[String(k)] = { lat, lon }
          }
        }
        gisCache = cache
        console.log(`[XLSX] GIS data loaded from JSON. Records: ${Object.keys(cache).length}`)
        return cache
      }
    } catch {
    }
    console.log(`[XLSX] Loading GIS data from /data/GISBLOCK.xlsx?t=${timestamp}`);
    const res = await fetch(`/data/GISBLOCK.xlsx?t=${timestamp}`)
    if (!res.ok) throw new Error("Failed to fetch GISBLOCK.xlsx")
    const buf = await res.arrayBuffer()
    const wb = XLSX.read(buf, { type: "array", raw: false })

    const cache: Record<string, GisData> = {}

    function ingestBlockSheet(ws: XLSX.WorkSheet) {
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" }) as any[][]
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i]
        const d = String(row[0] || "").trim().toLowerCase()
        const b = String(row[1] || "").trim().toLowerCase()
        const lat = parseFloat(row[2])
        const lon = parseFloat(row[3])
        if (d && b && validLatLon(lat, lon)) {
          cache[`${d}|${b}`] = { lat, lon }
        }
      }
    }

    function ingestGpSheet(ws: XLSX.WorkSheet) {
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" }) as any[][]
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i]
        const d = String(row[0] || "").trim().toLowerCase()
        const b = String(row[1] || "").trim().toLowerCase()
        const g = String(row[2] || "").trim().toLowerCase()
        const lat = parseFloat(row[3])
        const lon = parseFloat(row[4])
        if (d && b && g && validLatLon(lat, lon)) {
          cache[`${d}|${b}|${g}`] = { lat, lon }
        }
      }
    }

    const sheetNames = wb.SheetNames || []
    const blocksSheetName = sheetNames.find((n) => n.toLowerCase().includes("block")) || sheetNames[0]
    const gpsSheetName = sheetNames.find((n) => n.toLowerCase().includes("gp"))

    if (blocksSheetName) {
      ingestBlockSheet(wb.Sheets[blocksSheetName])
    }
    if (gpsSheetName) {
      ingestGpSheet(wb.Sheets[gpsSheetName])
    }
    gisCache = cache
    console.log(`[XLSX] GIS data loaded successfully. Records: ${Object.keys(cache).length}`);
    return cache
  } catch (err) {
    console.error("Failed to load GIS data", err)
    return {}
  }
}

// --- Cache ---

let bsCache: Record<string, BsData> | null = null
let zpCache: Record<string, ZpData> | null = null
// --- BS & ZP Implementation ---

export async function loadBsData(): Promise<Record<string, BsData>> {
  if (bsCache) return bsCache
  try {
    const jsonCandidates = dedupeUrls(buildAssetCandidates("data/bs.min.json"))
    for (const url of jsonCandidates) {
      try {
        console.log("FETCH URL:", url)
        const jsonRes = await fetchNoStore(url)
        if (!jsonRes.ok) continue
        const ct = (jsonRes.headers.get("content-type") || "").toLowerCase()
        if (ct.includes("text/html")) continue
        const text = await jsonRes.text()
        const trimmed = text.trimStart()
        if (trimmed.startsWith("<!doctype") || trimmed.startsWith("<html") || trimmed.startsWith("<")) continue
        const j = JSON.parse(text)
        if (j && typeof j === "object") {
          bsCache = j as Record<string, BsData>
          return bsCache
        }
      } catch {
        continue
      }
    }
    const { url, buf } = await loadFirstAvailableBinary(
      "BS",
      dedupeUrls([
        ...buildAssetCandidates("data/PSLANC.xlsx"),
        ...buildAssetCandidates("data/PSL.xlsx"),
        ...buildAssetCandidates("data/pslanc.xlsx"),
        ...buildAssetCandidates("data/psl.xlsx"),
      ]),
      15_000,
    )
    let wb: XLSX.WorkBook
    try {
      wb = XLSX.read(buf, { type: "array", raw: false })
    } catch (e: any) {
      throw new BsZpLoadError("PARSE_FAILED", "Failed to parse PSL.xlsx", { url, error: e instanceof Error ? e.message : String(e) })
    }
    const ws = wb.Sheets[wb.SheetNames[0]]
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" }) as any[][]

    const cache: Record<string, BsData> = {}

    let start = 0
    const hdr = (rows[0] || []).map(c => String(c).toLowerCase())
    if (hdr.some(c => c.includes("district") || c.includes("जिला") || c.includes("ਜ਼ਿਲ੍ਹਾ"))) {
      start = 1
    }

    for (let i = start; i < rows.length; i++) {
      const { d, b, c, v, wards } = parseBsZpRow(rows[i], 'bs')
      if (!d.en || !b.en) continue

      // Key by lowercase district|block for easy lookup
      const key = `${d.en.toLowerCase()}|${b.en.toLowerCase()}`
      cache[key] = {
        id: b.en,
        names: b,
        districtId: d.en,
        chairperson: { names: c },
        viceChairperson: (v?.en || v?.hi || v?.pa) ? { names: v } : undefined,
        wards
      }
    }
    if (Object.keys(cache).length === 0) {
      throw new BsZpLoadError("BAD_FILE", "PSL.xlsx parsed but produced no records", { url, rows: rows.length })
    }
    bsCache = cache
    appLogger.info("BS data loaded", { category: "data", meta: { source: url, rows: rows.length, records: Object.keys(cache).length } })
    return cache
  } catch (err: any) {
    const meta = err instanceof BsZpLoadError ? err.meta : { error: err instanceof Error ? err.message : String(err) }
    appLogger.error("Failed to load BS data", { category: "data", meta })
    throw err
  }
}

export async function loadZpData(): Promise<Record<string, ZpData>> {
  if (zpCache) return zpCache
  try {
    const jsonCandidates = dedupeUrls(buildAssetCandidates("data/zp.min.json"))
    for (const url of jsonCandidates) {
      try {
        console.log("FETCH URL:", url)
        const jsonRes = await fetchNoStore(url)
        if (!jsonRes.ok) continue
        const ct = (jsonRes.headers.get("content-type") || "").toLowerCase()
        if (ct.includes("text/html")) continue
        const text = await jsonRes.text()
        const trimmed = text.trimStart()
        if (trimmed.startsWith("<!doctype") || trimmed.startsWith("<html") || trimmed.startsWith("<")) continue
        const j = JSON.parse(text)
        if (j && typeof j === "object") {
          zpCache = j as Record<string, ZpData>
          return zpCache
        }
      } catch {
        continue
      }
    }
    const { url, buf } = await loadFirstAvailableBinary(
      "ZP",
      dedupeUrls([
        ...buildAssetCandidates("data/ZPLANC.xlsx"),
        ...buildAssetCandidates("data/ZPL.xlsx"),
        ...buildAssetCandidates("data/zplanc.xlsx"),
        ...buildAssetCandidates("data/zpl.xlsx"),
      ]),
      15_000,
    )
    let wb: XLSX.WorkBook
    try {
      wb = XLSX.read(buf, { type: "array", raw: false })
    } catch (e: any) {
      throw new BsZpLoadError("PARSE_FAILED", "Failed to parse ZPL.xlsx", { url, error: e instanceof Error ? e.message : String(e) })
    }
    const ws = wb.Sheets[wb.SheetNames[0]]
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" }) as any[][]

    const cache: Record<string, ZpData> = {}

    let start = 0
    const hdr = (rows[0] || []).map(c => String(c).toLowerCase())
    if (hdr.some(c => c.includes("district") || c.includes("जिला") || c.includes("ਜ਼ਿਲ੍ਹਾ"))) {
      start = 1
    }

    for (let i = start; i < rows.length; i++) {
      const { d, c, v, wards } = parseBsZpRow(rows[i], 'zp')
      if (!d.en) continue

      const key = d.en.toLowerCase()
      cache[key] = {
        id: d.en,
        names: d,
        chairperson: { names: c },
        viceChairperson: (v?.en || v?.hi || v?.pa) ? { names: v } : undefined,
        wards
      }
    }
    if (Object.keys(cache).length === 0) {
      throw new BsZpLoadError("BAD_FILE", "ZPL.xlsx parsed but produced no records", { url, rows: rows.length })
    }
    zpCache = cache
    appLogger.info("ZP data loaded", { category: "data", meta: { source: url, rows: rows.length, records: Object.keys(cache).length } })
    return cache
  } catch (err: any) {
    const meta = err instanceof BsZpLoadError ? err.meta : { error: err instanceof Error ? err.message : String(err) }
    appLogger.error("Failed to load ZP data", { category: "data", meta })
    throw err
  }
}

export function clearXlsxCaches() {
  fetchCacheBuster = Date.now()
  bsCache = null
  zpCache = null
  gisCache = null
}
